import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {JSFeatureFlag} from '@github-ui/feature-flags/client-feature-flags'
import type {UserState} from '@github-ui/swp-core/lib/utils/rendering-strategy'
import type {PersonalisationRule} from '../../types/components/personalisationRule'

type HeroCta = {fields: {href: string; text: string}}
type AudienceCondition = PersonalisationRule['fields']['audienceCondition']
type ResolvedHeroCta = {
  cta: HeroCta | undefined
  audienceCondition: AudienceCondition | undefined
}

const previewFlagByAudienceCondition: Record<AudienceCondition, JSFeatureFlag> = {
  COPILOT_BUSINESS: 'flex_suite_hero_cta_preview_copilot_business',
  COPILOT_EDU: 'flex_suite_hero_cta_preview_copilot_edu',
  COPILOT_ENTERPRISE: 'flex_suite_hero_cta_preview_copilot_enterprise',
  COPILOT_FREE: 'flex_suite_hero_cta_preview_copilot_free',
  COPILOT_MAX: 'flex_suite_hero_cta_preview_copilot_max',
  COPILOT_PRO: 'flex_suite_hero_cta_preview_copilot_pro',
  COPILOT_PRO_PLUS: 'flex_suite_hero_cta_preview_copilot_pro_plus',
  ENTERPRISE_ADMIN: 'flex_suite_hero_cta_preview_enterprise_admin',
  ENTERPRISE_MEMBER: 'flex_suite_hero_cta_preview_enterprise_member',
  LOGGED_IN: 'flex_suite_hero_cta_preview_logged_in',
  TEAM_ADMIN: 'flex_suite_hero_cta_preview_team_admin',
  TEAM_MEMBER: 'flex_suite_hero_cta_preview_team_member',
}

export function resolveCtaWithPersonalisation(
  defaultCta: HeroCta,
  personalisationRules: PersonalisationRule[] | undefined,
  userState: UserState,
): ResolvedHeroCta & {cta: HeroCta}
export function resolveCtaWithPersonalisation(
  defaultCta: HeroCta | undefined,
  personalisationRules: PersonalisationRule[] | undefined,
  userState: UserState,
): ResolvedHeroCta
export function resolveCtaWithPersonalisation(
  defaultCta: HeroCta | undefined,
  personalisationRules: PersonalisationRule[] | undefined,
  userState: UserState,
): ResolvedHeroCta {
  const hasPreviewOverride = Object.values(previewFlagByAudienceCondition).some(isFeatureEnabled)
  const matchingRule = personalisationRules?.find(rule => matchesAudienceCondition(rule, userState, hasPreviewOverride))

  return {
    cta: matchingRule?.fields.link ?? defaultCta,
    audienceCondition: matchingRule?.fields.audienceCondition,
  }
}

function matchesAudienceCondition(
  rule: PersonalisationRule,
  userState: UserState,
  hasPreviewOverride: boolean,
): boolean {
  const audienceCondition = rule.fields.audienceCondition
  if (hasPreviewOverride) return isFeatureEnabled(previewFlagByAudienceCondition[audienceCondition])

  return matchesUserState(audienceCondition, userState)
}

function matchesUserState(audienceCondition: AudienceCondition, userState: UserState): boolean {
  switch (audienceCondition) {
    case 'LOGGED_IN':
      return userState.isLoggedIn === true
    case 'COPILOT_EDU':
      return userState.hasCopilotEdu === true
    case 'COPILOT_FREE':
      return userState.hasCopilotFree === true
    case 'COPILOT_PRO':
      return userState.hasCopilotPro === true
    case 'COPILOT_PRO_PLUS':
      return userState.hasCopilotProPlus === true
    case 'COPILOT_MAX':
      return userState.hasCopilotMax === true
    case 'COPILOT_BUSINESS':
      return userState.hasCopilotBusiness === true
    case 'COPILOT_ENTERPRISE':
      return userState.hasCopilotEnterprise === true
    case 'TEAM_ADMIN':
      return userState.isTeamAdmin === true
    case 'ENTERPRISE_ADMIN':
      return userState.isEnterpriseAdmin === true
    case 'TEAM_MEMBER':
      return userState.isTeamMember === true
    case 'ENTERPRISE_MEMBER':
      return userState.isEnterpriseMember === true
    default:
      return false
  }
}
